import DesignPicker, { DesignThumb, type PickerRecipe } from '@/components/DesignPicker';
import { GarmentThumb, LocationChips } from '@/components/LineupGarmentPicker';
import { samePositionConflict, colorSummary, MAX_DESIGNS, keepsSchoolLogo } from '@/lib/assign-products';
import type { LineupPickerCard } from '@/lib/lineup-thumbs';

export type AssignDesign = {
  designTemplateId: string;
  position: string;
  titleName?: string;
  label?: string;
};

export type AssignStyle = {
  styleNumber: string;
  colors?: Record<string, boolean>;
  designs?: AssignDesign[];
  keepLogoProduct?: boolean;
};

export default function AssignGarmentCard({
  style,
  card,
  recipes,
  sportRecipes,
  sport,
  logoOnly,
  picking,
  highlighted,
  onPickSlot,
  onSetDesign,
  onSetPosition,
  onRemoveDesign,
  onKeepLogo,
  onRemove,
}: {
  style: AssignStyle;
  card?: LineupPickerCard;
  recipes: PickerRecipe[];
  sportRecipes: PickerRecipe[];
  sport?: string;
  logoOnly: boolean;
  picking: number | null;
  highlighted?: boolean;
  onPickSlot: (slot: number | null) => void;
  onSetDesign: (slot: number, recipeId: string) => void;
  onSetPosition: (slot: number, position: string) => void;
  onRemoveDesign: (slot: number) => void;
  onKeepLogo: (on: boolean) => void;
  onRemove: () => void;
}) {
  const designs = style.designs || [];
  const conflict = samePositionConflict(designs);
  const colors = colorSummary(style.colors);
  const slots = [0, 1];

  return (
    <div className={`rounded-md border p-3 ${highlighted ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}>
      <div className="mb-3 flex flex-wrap gap-3">
        <GarmentThumb src={card?.image || ''} alt={card?.name || style.styleNumber} className="h-20 w-20" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start gap-2">
            <div className="min-w-0">
              <div className="text-[14px] font-semibold tracking-tight">{card?.name || style.styleNumber}</div>
              <div className="font-mono text-[11px] text-muted">
                {style.styleNumber}{card?.brand ? ` · ${card.brand}` : ''}
              </div>
            </div>
            <button onClick={onRemove} className="ml-auto rounded-sm border border-border bg-white px-2 py-1 text-[11px] text-red-600 hover:bg-bg">
              remove
            </button>
          </div>
          <div className="mt-1.5">
            <LocationChips locations={card?.locations || []} empty="No logo locations in catalog — add them on the garment before binding designs." />
          </div>
          {colors && <div className="mt-1 text-[10px] text-subtle">Colors from lineup: {colors}</div>}
          <label className="mt-2 flex items-center gap-2 text-[12px]" title="Also sell the plain school-logo version of this garment">
            <input type="checkbox" checked={keepsSchoolLogo(style)} disabled={logoOnly} onChange={(e) => onKeepLogo(e.target.checked)} />
            Also sell school-logo version
          </label>
        </div>
      </div>

      {logoOnly ? (
        <div className="text-[11px] text-subtle">Logo-only family — designs are not attached.</div>
      ) : (
        <>
          {conflict && (
            <div className="mb-2 text-[11px] text-red-600">Both designs use the same print location — pick different spots or they become two Shopify products of the same card.</div>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            {slots.map((slot) => {
              const d = designs[slot];
              const recipe = d ? recipes.find((r) => r.id === d.designTemplateId) : undefined;
              const open = picking === slot;
              return (
                <div key={slot} className="rounded-md border border-border bg-white p-2">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.04em] text-subtle">
                    Design {slot + 1}{slot === 1 ? ' (optional)' : ''}
                  </div>
                  {d ? (
                    <div className="flex gap-2">
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-sm border border-border bg-white">
                        {recipe ? <DesignThumb recipe={recipe} /> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[11px] font-medium">{d.titleName || d.label || d.designTemplateId}</div>
                        <div className="mt-1">
                          <LocationChips
                            locations={card?.locations || []}
                            empty="No catalog location"
                            selected={d.position}
                            onSelect={(pos) => onSetPosition(slot, pos)}
                          />
                        </div>
                        <div className="mt-1 flex gap-1">
                          <button type="button" onClick={() => onPickSlot(open ? null : slot)} className="text-[10px] text-primary underline">
                            {open ? 'close' : 'change'}
                          </button>
                          <button type="button" onClick={() => onRemoveDesign(slot)} className="text-[10px] text-red-600 underline">
                            remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onPickSlot(open ? null : slot)}
                      disabled={!card?.locations.length || (slot === 1 && !designs[0])}
                      title={!card?.locations.length ? 'This blank has no print location in the catalog' : slot === 1 && !designs[0] ? 'Add design 1 first' : undefined}
                      className="flex h-[72px] w-full items-center justify-center rounded-sm border border-dashed border-border text-[12px] text-muted hover:bg-bg disabled:opacity-50"
                    >
                      {open ? 'Close picker' : slot === 1 && !designs[0] ? 'Add design 1 first' : `Add design${slot === 0 ? '' : ` (${MAX_DESIGNS} max)`}`}
                    </button>
                  )}
                  {open && (
                    <div className="mt-2 border-t border-border pt-2">
                      <DesignPicker
                        recipes={sportRecipes}
                        value={d?.designTemplateId || null}
                        defaultSport={sport}
                        cols={3}
                        maxHeight={280}
                        onChange={(v) => onSetDesign(slot, Array.isArray(v) ? v[0] : v)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
